import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@quintoandar-tokko/jopijs';
import PendingCard from '@/components/Dashboard/DashboardPendings/PendingsCard';
import { getOverviewPendingEvents } from '@/store/slices/components/dashboard/service';
import PendingScheduledEventRow from './PendingScheduledEventRow';
import OtherTooltip from '../../OtherTooltip';
import useFetchDashboard from '@/components/Dashboard/customHooks/useFetchDashboard';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import styles from './pendingScheduledEvents.module.scss';
import DashboardEmptyStateCard from '@/components/Dashboard/DashboardEmptyStateCard';
import CalendarIcon from '@/public/images/dashboard/calendar.svg';
import { toggleModal } from '@/store/slices/components/calendarEvents/CalendarEventsSlice';
import { getAgent } from '@/store/slices/components/calendarEvents/services';
import { AGENT } from '@/components/FilterBar/constants/filtersConstants';
import { useTrackOverviewTab } from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function PendingScheduledEvents() {
  const dispatch = useDispatch();
  const { pendingScheduledEvents } = useSelector(
    (state) => state.DashboardSlice
  );
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const track = useTrackOverviewTab();

  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();
  const tokkoBrokerHost = localStorage.getItem('tokkoBrokerHost');
  const { list, others, loading, hasFetched, empty } = pendingScheduledEvents;

  useFetchOnViewport(
    (query) => {
      dispatch(getOverviewPendingEvents(query));
    },
    [],
    { hasFetched }
  );

  const othersIds = others.map((event) => event.id);
  const othersCount = others.reduce((acc, curr) => acc + curr.count, 0);
  const othersPercentage = others.reduce(
    (acc, curr) => acc + curr.percentage,
    0
  );

  const getHeaderUrl = () => {
    let url = `${tokkoBrokerHost}/schedule/full_calendar`;
    const onlyOneAgent =
      selectedIds.find((el) => el.filter_type === AGENT) &&
      selectedIds.length === 1;
    const agent = onlyOneAgent ? selectedIds[0] : 'all';

    url += `?agent=${agent}`;

    return url;
  };

  const openCalendarView = (idToRedirect) => {
    let url = `${tokkoBrokerHost}/schedule/full_calendar?event_type=${idToRedirect}`;
    const onlyOneAgent =
      selectedIds.find((el) => el.filter_type === AGENT) &&
      selectedIds.length === 1;
    if (onlyOneAgent) {
      url += `&agent=${selectedIds[0]}`;
    }

    if (selectedIds.length === 0) {
      url += '&agent=all';
    }

    window.open(url, '_blank');
  };

  const getTooltipContent = () => {
    const maxVisible = 12;
    const visible = others.slice(0, maxVisible);
    const remaining = others.length - maxVisible;

    return (
      <>
        {visible.map((event) => (
          <Text key={event.id}>{`${event.name}: ${event.count}`}</Text>
        ))}
        {remaining > 0 && (
          <Text>{`${Trans(translations, 'CountMore', remaining)}...`}</Text>
        )}
      </>
    );
  };

  return (
    <PendingCard ref={ref} loading={loading || isStale}>
      <PendingCard.Header
        redirectUrl={!empty && getHeaderUrl()}
        toolTipText={Trans(translations, 'ShowAll')}
        trackCallback={(element) => track.pendingScheduledEvents(element)}
      >
        {Trans(translations, 'PendingScheduledEvents')}
      </PendingCard.Header>
      <Box className={styles.pendingScheduledEvents}>
        {empty ? (
          <DashboardEmptyStateCard
            icon={CalendarIcon}
            title={Trans(translations, 'HereYouWillSeeTheTypesOfPendingEvents')}
            buttonText={Trans(translations, 'CreateEvent')}
            onButtonClick={() => {
              dispatch(toggleModal());

              const ids = selectedIds.length > 0 ? selectedIds : null;
              if (ids) {
                dispatch(getAgent(ids));
              }
            }}
          />
        ) : (
          <>
            {list.map((event) => (
              <PendingScheduledEventRow
                key={event.id}
                stats={{
                  text: event.name,
                  count: event.count,
                  percentage: event.percentage,
                }}
                onClick={
                  selectedIds.length > 1
                    ? null
                    : () => {
                        openCalendarView(event.id);
                        track.pendingScheduledEvents(TRACK_ELEMENT.ROW);
                      }
                }
                data-testid={`pending-scheduled-event-${event.id}`}
              />
            ))}

            {others.length > 0 && (
              <Box className={styles.othersRow}>
                <PendingScheduledEventRow
                  stats={{
                    text: Trans(translations, 'Others'),
                    count: othersCount,
                    percentage: othersPercentage,
                  }}
                  className={styles.othersRow}
                  onClick={
                    selectedIds.length > 1
                      ? null
                      : () => {
                          openCalendarView(othersIds);
                          track.pendingScheduledEvents(TRACK_ELEMENT.ROW);
                        }
                  }
                  data-testid="pending-scheduled-event-row-others"
                />

                <OtherTooltip className={styles.tooltip} xAxisPosition="right">
                  {getTooltipContent()}
                </OtherTooltip>
              </Box>
            )}
          </>
        )}
      </Box>
    </PendingCard>
  );
}
